// filepath: apps/web/src/features/proc/CancelInstanceButton.tsx
//
// FR-UI-PROC-1 AC-5 — cancel-instance action button for a failed
// instance row.
//
// Cancel is destructive (terminates the BPM instance state machine).
// The button renders an inline confirmation dialog so the user
// must explicitly confirm before the mutation fires. The dialog
// is rendered via a native <dialog> element where available
// (browser-supported) so focus management and Esc-to-cancel work
// without a custom focus-trap implementation; in jsdom + older
// browsers the dialog renders as a normal div but the user still
// must click "Confirm" before the mutation fires.
//
// The button invokes `useCancelInstance` on confirm. It is disabled
// while the mutation is in flight (with explanatory chrome).

import React, { useRef } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { useCancelInstance } from "./useCancelInstance";
import type { FailedInstance } from "./types";

export interface CancelInstanceButtonProps {
  instance: FailedInstance;
  /**
   * Called when the mutation succeeds. The parent (row) uses this
   * to invalidate TanStack Query and refresh the list view.
   */
  onSuccess?: () => void;
}

export function CancelInstanceButton({
  instance,
  onSuccess,
}: CancelInstanceButtonProps): React.ReactElement {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const { mutate, isPending, error } = useCancelInstance({
    onSuccess: () => onSuccess?.(),
  });

  function openConfirm(): void {
    const dialog = dialogRef.current;
    if (dialog !== null && typeof dialog.showModal === "function") {
      dialog.showModal();
    }
  }

  function closeConfirm(): void {
    const dialog = dialogRef.current;
    if (dialog !== null && typeof dialog.close === "function") {
      dialog.close();
    }
  }

  function confirmCancel(): void {
    mutate({ instanceId: instance.id });
    closeConfirm();
  }

  const triggerLabel = isPending
    ? t("failed_instance.cancel_pending", "Cancelling…")
    : t("failed_instance.cancel_button", "Cancel instance");

  return (
    <>
      <button
        type="button"
        data-testid="cancel-instance-button"
        data-instance-id={instance.id}
        disabled={isPending}
        aria-busy={isPending}
        onClick={openConfirm}
        className="cancel-instance-button"
      >
        {triggerLabel}
      </button>
      {error !== null && (
        <span
          data-testid="cancel-instance-button-error"
          className="cancel-instance-button-error"
          role="alert"
        >
          {t("failed_instance.cancel_error", "Cancel failed.")}
        </span>
      )}
      <dialog
        ref={dialogRef}
        data-testid="cancel-instance-confirm"
        className="cancel-instance-confirm-dialog"
        aria-labelledby="cancel-instance-confirm-title"
      >
        <h2
          id="cancel-instance-confirm-title"
          className="cancel-instance-confirm-title"
        >
          {t("failed_instance.cancel_confirm_title", "Cancel this instance?")}
        </h2>
        <p className="cancel-instance-confirm-body">
          {t(
            "failed_instance.cancel_confirm_body",
            "This terminates the process instance. The failure record will be retained for audit; no further steps will execute.",
          )}
        </p>
        <div className="cancel-instance-confirm-actions">
          <button
            type="button"
            data-testid="cancel-instance-confirm-no"
            onClick={closeConfirm}
            disabled={isPending}
          >
            {t("common.no", "No")}
          </button>
          <button
            type="button"
            data-testid="cancel-instance-confirm-yes"
            onClick={confirmCancel}
            disabled={isPending}
          >
            {t("common.yes", "Yes")}
          </button>
        </div>
      </dialog>
    </>
  );
}
